import dayjs from "dayjs";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";

import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { KEYWORD_GROUP_PREFIX, keywordGroupsKeys } from "app/services/keyword-groups";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import {
    ChangePercentage,
    DefaultCellRightAlign,
    LinkCell,
    TrafficShareWithVisits,
    WebsiteTooltipTopCell,
} from "app/components/React/Table/cells";
import { CompareBarCell } from "app/components/React/Table/cells/CompareBarCell";
import { KaKeywordPosition } from "app/components/React/Table/cells/KaKeywordPosition";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberVisitsFilter, minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import { TableWidget } from "./TableWidget";
import { TableTemplateV2 } from "../dashboard/TableTemplate-v2";

import KeywordAnalysisDataFetcherFactory from "app/components/widget/widget-fetchers/KeywordAnalysisDataFetcherFactory";

import { TableKeywordAnalysisDashboardWidgetFilters } from "./TableKeywordAnalysisDashboardWidgetFilter";

import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import DurationService, { BasicDurations, apiFormat, customRangeFormat } from "app/services/DurationService";
import { LeftFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { ISubtitleTextItem } from "./Widget";
import {
    KeywordAnalysisTableWidgetAPIParams,
    KeywordAnalysisWidgetFiltersMap,
} from "./TableKeywordAnalysisDashboardConst";

import { WebsiteKeywordsTableTabSubtitle } from "../widget-subtitle-templates/WebsiteKeywordsTableTabSubtitle";

import type { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import {
    TABLE_COLUMN_CHANGE,
    TABLE_COLUMN_DOMAIN,
    TABLE_COLUMN_KEYWORDS,
    TABLE_COLUMN_ORGANIC_CLICKS_SHARE,
    TABLE_COLUMN_POSITION,
    TABLE_COLUMN_SHARE,
    TABLE_COLUMN_TOP_URL,
    TABLE_COLUMN_URL,
    TABLE_COLUMN_WEB_SOURCE_SHARE,
} from "app/common/keyword-research/serp-players/consts";
import { getColumnTexts } from "app/common/keyword-research/domains-table/columnTexts";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import queryClient from "single-spa/react-app/query-client";

const MIN_CLICKS = 1;
type ColumnTooltipMode = "group" | "single";
const columnTitle = "title";
export const DefaultCellRightAlignWithPadding = styled(DefaultCellRightAlign)`
    padding-right: 10px;
`;

export const WidgetFilter = styled.div`
    max-width: 280px;
`;
const WaKeywordPositionStyled = styled(KaKeywordPosition)`
    text-align: left;
`;

const ChangePercentageWrapper = styled(LeftFlexRow)`
    height: 100%;
`;

export enum TableType {
    Urls,
    Domains,
}

export class TableKeywordAnalysisDashboardWidget extends TableWidget {
    private allKeywordGroupsCache: Map<string, IUserKeywordGroup> = new Map();

    constructor() {
        super();
    }

    public getSearchKey() {
        return "SearchTerm";
    }

    async runWidget() {
        await this.prefetchAllKeywordGroups();
        if (this._autoFetchData) {
            this.getData();
        }
        this.setMetadata();
        this.getLegendImage();
    }

    private async prefetchAllKeywordGroups(): Promise<void> {
        try {
            const userGroups = queryClient.getQueryData<IUserKeywordGroup[]>(keywordGroupsKeys.user()) || [];
            const sharedGroups = queryClient.getQueryData<IUserKeywordGroup[]>(keywordGroupsKeys.shared()) || [];
            const keywordsGroupList: IUserKeywordGroup[] = [...userGroups, ...sharedGroups];
            keywordsGroupList.forEach((group) => {
                if (group.Id) {
                    this.allKeywordGroupsCache.set(group.Id, group);
                }
            });
        } catch (error) {
            Sentry.error("Error prefetching keyword groups", error);
        }
    }

    private getKeywordGroupFromCache(groupId: string): IUserKeywordGroup | undefined {
        return this.allKeywordGroupsCache.get(groupId);
    }
    public isPptSupported = () => {
        return false;
    };
    static getWidgetMetadataType() {
        return "TableKeywordAnalysisDashboard";
    }

    static getWidgetDashboardType() {
        return "Table";
    }

    static getWidgetResourceType() {
        return "Table";
    }

    protected getDataFetcherFactory() {
        return KeywordAnalysisDataFetcherFactory;
    }

    static getFiltersComponent() {
        return TableKeywordAnalysisDashboardWidgetFilters;
    }

    get templateComponent() {
        return TableTemplateV2;
    }
    protected rowReducer(row) {
        const requestParams = this._params;

        const state = widgetSettings.getMetricProperties(requestParams.metric).state;
        const stateParams = {
            isWWW: "*",
            duration: DurationService.getDiffSymbol(
                requestParams.from,
                requestParams.to,
                requestParams.isWindow ? "days" : "months",
            ),
            country: requestParams.country,
            key: row.Domain,
        };
        if (row.Domain || row.Keyword) {
            const rowParams = {
                ...stateParams,
                keyword: row.Keyword || requestParams.keys,
            };
            return {
                ...row,
            };
        }
        return { ...row };
    }

    getSingleHeader = () => this.subtitleData;

    getSingleHeaderItem = () => this.subtitleData && this.subtitleData.TotalClicks;

    getAbbrNumberVisitsFilter = () => abbrNumberVisitsFilter();

    getMinVisitsAbbrFilter = () => minVisitsAbbrFilter();

    getPercentagesignFilter = () => percentageSignFilter();

    getColumnsConfig() {
        const { country, webSource, keyword, sourceType, isWindow, keys } = this.apiParams;
        const isKeywordGroup = !!this.getKeywordGroupFromCache(keys);
        const mode: ColumnTooltipMode = isKeywordGroup ? "group" : "single";

        const isTotalWebSourceSelected = webSource === DEVICES_TYPES_FOR_API.TOTAL;
        const showPosition = parseInt(country) !== 999;
        const isTotalTabSelected = sourceType === SourceTypeMap.Total;
        const tableType = this._metricConfig.tableType;
        let columns = [];

        const sharedColumns = [
            {
                field: TABLE_COLUMN_SHARE,
                cellComponent: (props) => {
                    const row = props.row;
                    row.TotalVisits = row.Clicks;
                    return (
                        <TrafficShareWithVisits
                            {...props}
                            row={row}
                            thresholdForTotalFilter={50}
                            applyAbbrNumberFilter
                        />
                    );
                },
                headerComponent: DefaultCellHeader,
                title: getColumnTexts(TABLE_COLUMN_SHARE, columnTitle),
                tooltip: getColumnTexts(TABLE_COLUMN_SHARE, mode),
                width: 130,
                isResizable: false,
            },
            {
                field: TABLE_COLUMN_CHANGE,
                title: getColumnTexts(TABLE_COLUMN_CHANGE, columnTitle),
                cellComponent: (props) => (
                    <ChangePercentageWrapper>
                        <ChangePercentage {...props} />
                    </ChangePercentageWrapper>
                ),
                headerComponent: DefaultCellHeader,
                tooltip: getColumnTexts(TABLE_COLUMN_CHANGE, mode),
                width: 100,
            },

            ...(isTotalTabSelected
                ? [
                      {
                          name: TABLE_COLUMN_ORGANIC_CLICKS_SHARE,
                          title: getColumnTexts(TABLE_COLUMN_ORGANIC_CLICKS_SHARE, columnTitle),
                          cellComponent: (props) => {
                              return (
                                  <CompareBarCell
                                      shouldAddRightSpace={true}
                                      isSourceType={true}
                                      displayNoData={props.row.Clicks <= MIN_CLICKS}
                                      {...props}
                                  />
                              );
                          },
                          tooltip: getColumnTexts(TABLE_COLUMN_ORGANIC_CLICKS_SHARE, mode),
                          headerComponent: DefaultCellHeaderRightAlign,
                          width: 120,
                      },
                  ]
                : []),

            ...(isTotalWebSourceSelected
                ? [
                      {
                          field: TABLE_COLUMN_WEB_SOURCE_SHARE,
                          title: getColumnTexts(TABLE_COLUMN_WEB_SOURCE_SHARE, columnTitle),
                          cellComponent: (props) => (
                              <CompareBarCell
                                  shouldAddRightSpace={true}
                                  displayNoData={props.row.Clicks === 0}
                                  {...props}
                              />
                          ),
                          headerComponent: DefaultCellHeader,
                          tooltip: getColumnTexts(TABLE_COLUMN_WEB_SOURCE_SHARE, mode),
                          width: 140,
                      },
                  ]
                : []),
            ...(showPosition
                ? [
                      {
                          name: TABLE_COLUMN_POSITION,
                          field: TABLE_COLUMN_POSITION,
                          title: getColumnTexts(TABLE_COLUMN_POSITION, columnTitle),
                          cellComponent: WaKeywordPositionStyled,
                          tooltip: getColumnTexts(TABLE_COLUMN_POSITION, mode),
                          format: "minusNAFilter",
                          width: 80,
                      },
                  ]
                : []),
        ];

        if (tableType === TableType.Domains) {
            columns = [
                {
                    width: 195,
                    field: TABLE_COLUMN_DOMAIN,
                    title: getColumnTexts(TABLE_COLUMN_DOMAIN, columnTitle),
                    tooltip: getColumnTexts(TABLE_COLUMN_DOMAIN, mode),
                    isResizable: true,
                    cellComponent: (props) => {
                        return (
                            <WebsiteTooltipTopCell
                                hideDomainCount={true}
                                {...props}
                            />
                        );
                    },
                    fixed: true,
                    headerComponent: DefaultCellHeader,
                },
                ...sharedColumns,

                {
                    field: TABLE_COLUMN_TOP_URL,
                    title: getColumnTexts(TABLE_COLUMN_TOP_URL, columnTitle),
                    cellComponent: LinkCell,
                    headerComponent: DefaultCellHeader,
                    cellClass: "url-cell",
                    tooltip: getColumnTexts(TABLE_COLUMN_TOP_URL, mode),
                    minWidth: 200,
                },
            ];
        } else {
            columns = [
                {
                    name: TABLE_COLUMN_URL,
                    field: TABLE_COLUMN_URL,
                    title: getColumnTexts(TABLE_COLUMN_URL, columnTitle),
                    tooltip: getColumnTexts(TABLE_COLUMN_URL, mode),
                    cellComponent: LinkCell,
                    cellClass: "url-cell",
                    minWidth: 280,
                },
                ...(isKeywordGroup
                    ? [
                          {
                              field: TABLE_COLUMN_KEYWORDS,
                              cellComponent: (props) => {
                                  const { row } = props;
                                  return (
                                      <DefaultCellRightAlignWithPadding
                                          {...props}
                                          value={row.Keywords?.length}
                                      />
                                  );
                              },
                              headerComponent: DefaultCellHeaderRightAlign,
                              title: getColumnTexts(TABLE_COLUMN_KEYWORDS, columnTitle),
                              tooltip: getColumnTexts(TABLE_COLUMN_KEYWORDS, mode),
                              width: 80,
                          },
                      ]
                    : []),
                ...sharedColumns,
            ];
        }
        return columns.filter(Boolean);
    }
    callbackOnGetData(response: any) {
        response.Filters.Categories = response.Categories;
        response.Filters.DomainsList = response.DomainsList;

        super.callbackOnGetData(response);
    }

    getViewData() {
        super.getViewData();

        const { webSource, country } = this._widgetConfig.properties;

        if (!country && !webSource) {
            //in case devices was not selected yet
            return this.viewData;
        }
        this.viewData.subtitleTextItem = this.getFiltersTooltip();
        this.viewData.customSubtitle = "true";
        this.viewData.customSubtitleComponent = WebsiteKeywordsTableTabSubtitle;
        return this.viewData;
    }

    getFiltersTooltip(): ISubtitleTextItem {
        const mappedFilters = KeywordAnalysisTableWidgetAPIParams.map((filter) =>
            KeywordAnalysisWidgetFiltersMap[filter](this._params),
        );
        const availableFilters = mappedFilters.filter((filter) => filter !== null);
        const amountOfFilters = availableFilters.length;
        if (!amountOfFilters) {
            return;
        }

        const filtersTextSubtitle = (() => {
            switch (amountOfFilters) {
                case 0:
                    return availableFilters[0];
                case 1:
                    return translate("dashboard.widget.subtitle.landing.page.filters.unique");
                default:
                    return translate("dashboard.widget.subtitle.landing.page.filters.multiple", {
                        amount: amountOfFilters,
                    });
            }
        })();

        const tooltipValue = availableFilters.map((filter, index) => <WidgetFilter key={index}>{filter}</WidgetFilter>);
        return {
            text: filtersTextSubtitle,
            tooltipValue,
        };
    }

    public getProUrl() {
        const { country, webSource, to, latest, keys, keyword, source, sourceType, category, funcFlag, domains } =
            this.apiParams;

        const sourceKey = Object.keys(SourceTypeMap).find(
            (key) => SourceTypeMap[key as keyof typeof EKeywordTabs] === sourceType,
        );
        const is28Days = latest === "28d";
        const selectedMonth = dayjs.utc(to, apiFormat).format(customRangeFormat);
        const isKeywordGroup = !!this.getKeywordGroupFromCache(keys);
        const formattedKeys = isKeywordGroup ? `${KEYWORD_GROUP_PREFIX}${keys}` : keys;

        const toParams = {
            country,
            duration: is28Days ? "28d" : `${selectedMonth}-${selectedMonth}`,
            webSource,
            key: formattedKeys,
            keyword: formattedKeys,
            source,
            selectedPageTab: sourceKey,
            funcFlag,
            category,
            domains,
            graphDuration: is28Days ? BasicDurations.LAST_TWENTY_EIGHT_DAYS : BasicDurations.LAST_MONTH,
            timeGranularity: GranularityNames.WEEKLY,
            ...(is28Days ? { latest: "28d" } : {}),
        };
        const tableType = this._metricConfig.tableType;
        const state =
            tableType === TableType.Domains ? "organicsearch_keywordAnalysis_2" : "organicsearch_trafficAnalysis_2";
        return this._swNavigator.getStateUrl(state, {
            ...toParams,
        });
    }
}

TableKeywordAnalysisDashboardWidget.register();
